import React, { useState } from 'react'
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Box,
  Avatar,
  Chip,
  Paper,
  TextField,
  Alert
} from '@mui/material'
import ReactMarkdown from 'react-markdown'
import {
  Spellcheck as GrammarIcon,
  Chat as ChatIcon,
  Edit as EditIcon,
  Assessment as AssessmentIcon,
  TrendingUp as ProgressIcon,
  Book as VocabularyIcon,
  MenuBook as ReadingIcon,
  Headphones as ListeningIcon,
  School as SchoolIcon,
  Star as StarIcon,
  Speed as SpeedIcon
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { apiClient, API_ENDPOINTS } from '../config/api'
import toast from 'react-hot-toast'

const features = [
  {
    title: 'Sửa ngữ pháp',
    description: 'Ligo sẽ phân tích và sửa lỗi ngữ pháp, giải thích chi tiết cách sửa',
    icon: <GrammarIcon />,
    path: '/grammar',
    color: '#4CAF50',
    gradient: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)'
  },
  {
    title: 'Luyện hội thoại',
    description: 'Trò chuyện với Ligo để cải thiện kỹ năng giao tiếp tiếng Anh',
    icon: <ChatIcon />,
    path: '/conversation',
    color: '#2196F3',
    gradient: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)'
  },
  {
    title: 'Chấm bài viết',
    description: 'Đánh giá bài viết theo tiêu chuẩn IELTS với phản hồi chi tiết',
    icon: <EditIcon />,
    path: '/writing',
    color: '#FF9800',
    gradient: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)'
  },
  {
    title: 'Học từ vựng',
    description: 'Mở rộng vốn từ vựng với giải thích và ví dụ thực tế',
    icon: <VocabularyIcon />,
    path: '/vocabulary',
    color: '#9C27B0',
    gradient: 'linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)'
  },
  {
    title: 'Luyện đọc hiểu',
    description: 'Bài tập đọc hiểu với các chủ đề đa dạng và câu hỏi thực tế',
    icon: <ReadingIcon />,
    path: '/reading',
    color: '#607D8B',
    gradient: 'linear-gradient(135deg, #607D8B 0%, #455A64 100%)'
  },
  {
    title: 'Luyện nghe',
    description: 'Cải thiện kỹ năng nghe với các bài tập và transcript',
    icon: <ListeningIcon />,
    path: '/listening',
    color: '#795548',
    gradient: 'linear-gradient(135deg, #795548 0%, #5D4037 100%)'
  },
  {
    title: 'Đánh giá năng lực',
    description: 'Kiểm tra trình độ tiếng Anh toàn diện với Ligo',
    icon: <AssessmentIcon />,
    path: '/assessment',
    color: '#F44336',
    gradient: 'linear-gradient(135deg, #F44336 0%, #D32F2F 100%)'
  },
  {
    title: 'Theo dõi tiến độ',
    description: 'Xem báo cáo chi tiết về quá trình học tập của bạn',
    icon: <ProgressIcon />,
    path: '/progress',
    color: '#009688',
    gradient: 'linear-gradient(135deg, #009688 0%, #00796B 100%)'
  }
]

const stats = [
  { label: 'Học viên đã sử dụng', value: '10,000+', icon: <SchoolIcon /> },
  { label: 'Bài tập đã tạo', value: '50,000+', icon: <StarIcon /> },
  { label: 'Độ chính xác AI', value: '95%', icon: <SpeedIcon /> }
]

function Home() {
  const navigate = useNavigate()
  const [levelTest, setLevelTest] = useState('')
  const [isAssessing, setIsAssessing] = useState(false)
  const [assessmentResult, setAssessmentResult] = useState(null)
  const [isStreaming, setIsStreaming] = useState(false)
  const [streamingAssessment, setStreamingAssessment] = useState('')

  const handleFeatureClick = (path) => {
    navigate(path)
  }

  const handleStreamingAssessment = async (data) => {
    if (data.type === 'chunk') {
      setStreamingAssessment(prev => prev + data.content)
    } else if (data.type === 'complete') {
      const completeAssessment = {
        original: levelTest,
        assessment: data.response,
        timestamp: data.timestamp
      }
      setAssessmentResult(completeAssessment)
      toast.success('Đánh giá trình độ thành công!')
    } else if (data.type === 'error') {
      console.error('Streaming assessment error:', data.error)
      toast.error('Có lỗi xảy ra khi đánh giá trình độ')
    }
  }

  const handleLevelAssessment = async () => {
    if (!levelTest.trim()) {
      toast.error('Vui lòng nhập đoạn văn để đánh giá trình độ')
      return
    }

    setIsAssessing(true)
    setIsStreaming(true)
    setStreamingAssessment('')
    setAssessmentResult(null)
    
    try {
      const response = await fetch(`${apiClient.defaults.baseURL}${API_ENDPOINTS.TEACHER.ASSESS_LEVEL_STREAM}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: levelTest
        })
      })

      if (!response.ok) {
        throw new Error('Network response was not ok')
      }

      if (!response.body) {
        throw new Error('No response body available for streaming')
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()

      while (true) {
        const { done, value } = await reader.read()
        
        if (done) break
        
        const chunk = decoder.decode(value)
        const lines = chunk.split('\n')
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6))
              await handleStreamingAssessment(data)
            } catch (parseError) {
              console.error('Error parsing SSE data:', parseError)
            }
          }
        }
      }
    } catch (error) {
      console.error('Level assessment error:', error)
      toast.error('Có lỗi xảy ra khi đánh giá trình độ')
    } finally {
      setIsAssessing(false)
      setIsStreaming(false)
      setStreamingAssessment('')
    }
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Avatar
            sx={{
              width: 80,
              height: 80,
              mx: 'auto',
              mb: 3,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              fontSize: '2rem'
            }}
          >
            🤖
          </Avatar>
          <Typography
            variant="h2"
            component="h1"
            sx={{
              mb: 2,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 700
            }}
          >
            Trung tâm tiếng Anh LIGO Edu - TT Quế, Kim bảng, Hà Nam
          </Typography>
          
          <Typography
            variant="h5"
            color="text.secondary"
            sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}
          >
            Học tiếng Anh thông minh với Ligo - Sửa ngữ pháp, luyện hội thoại, chấm bài viết và đánh giá IELTS
          </Typography>
          
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Chip
              label="Powered by LIGO EDU"
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 500 }}
            />
            <Chip
              label="Miễn phí sử dụng"
              color="success"
              variant="outlined"
              sx={{ fontWeight: 500 }}
            />
            <Chip
              label="Hỗ trợ 24/7"
              color="info"
              variant="outlined"
              sx={{ fontWeight: 500 }}
            />
          </Box>
        </Box>
      </motion.div>

      {/* Quick Level Assessment */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.8 }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            mb: 6,
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
            border: '1px solid rgba(102, 126, 234, 0.2)'
          }}
        >
          <Typography variant="h4" sx={{ mb: 2, textAlign: 'center', fontWeight: 600 }}>
            🎯 Kiểm tra trình độ nhanh
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, textAlign: 'center', color: 'text.secondary' }}>
            Viết một đoạn văn ngắn bằng tiếng Anh để Ligo đánh giá trình độ của bạn
          </Typography>
          
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Ví dụ: I love learning English because it helps me communicate with people from different countries. English is a global language that opens many opportunities for my career and personal development..."
            value={levelTest}
            onChange={(e) => setLevelTest(e.target.value)}
            sx={{ mb: 3 }}
          />
          
          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleLevelAssessment}
              disabled={isAssessing || isStreaming}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                px: 4,
                py: 1.5
              }}
            >
              {isStreaming ? '🔄 Đang phân tích...' : isAssessing ? 'Đang đánh giá...' : 'Đánh giá trình độ'}
            </Button>
          </Box>
          
          {/* Streaming Assessment Display */}
          {isStreaming && streamingAssessment && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Alert severity="info" sx={{ mt: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>🔄 Đang phân tích trình độ...</Typography>
                <Box sx={{ 
                  maxHeight: '300px', 
                  overflow: 'auto',
                  '& p': { margin: '8px 0' },
                  '& ul, & ol': { paddingLeft: '20px' },
                  '& li': { margin: '4px 0' },
                  '& h1, & h2, & h3': { margin: '12px 0 8px 0' },
                  '& strong': { fontWeight: 600 }
                }}>
                  <ReactMarkdown>{streamingAssessment}</ReactMarkdown>
                </Box>
              </Alert>
            </motion.div>
          )}

          {/* Final Assessment Result */}
          {assessmentResult && !isStreaming && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Alert severity="success" sx={{ mt: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>✅ Kết quả đánh giá trình độ</Typography>
                <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic' }}>
                  <strong>Đoạn văn gốc:</strong> {assessmentResult.original}
                </Typography>
                <Box sx={{ 
                  '& p': { margin: '8px 0' },
                  '& ul, & ol': { paddingLeft: '20px' },
                  '& li': { margin: '4px 0' },
                  '& h1, & h2, & h3': { margin: '12px 0 8px 0' },
                  '& strong': { fontWeight: 600 }
                }}>
                  <ReactMarkdown>{assessmentResult.assessment}</ReactMarkdown>
                </Box>
                {assessmentResult.timestamp && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 2, color: 'text.secondary' }}>
                    Đánh giá lúc: {new Date(assessmentResult.timestamp).toLocaleString('vi-VN')}
                  </Typography>
                )}
              </Alert>
            </motion.div>
          )}
        </Paper>
      </motion.div>

      {/* Stats Section */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.8 }}
      >
        <Grid container spacing={3} sx={{ mb: 6 }}>
          {stats.map((stat, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card
                sx={{
                  textAlign: 'center',
                  p: 3,
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.2)'
                }}
              >
                <Avatar
                  sx={{
                    width: 60,
                    height: 60,
                    mx: 'auto',
                    mb: 2,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                  }}
                >
                  {stat.icon}
                </Avatar>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  {stat.value}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {stat.label}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </motion.div>

      {/* Features Grid */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6, duration: 0.8 }}
      >
        <Typography
          variant="h3"
          component="h2"
          sx={{ mb: 4, textAlign: 'center', fontWeight: 600 }}
        >
          🚀 Tính năng nổi bật
        </Typography>
        
        <Grid container spacing={3}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index, duration: 0.6 }}
                whileHover={{ y: -5 }}
              >
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 6
                    }
                  }}
                  onClick={() => handleFeatureClick(feature.path)}
                >
                  <CardContent sx={{ flexGrow: 1, textAlign: 'center', p: 3 }}>
                    <Avatar
                      sx={
                        {
                          width: 60,
                          height: 60,
                          mx: 'auto',
                          mb: 2,
                          background: feature.gradient
                        }
                      }
                    >
                      {feature.icon}
                    </Avatar>
                    
                    <Typography
                      variant="h6"
                      component="h3"
                      sx={{ mb: 2, fontWeight: 600 }}
                    >
                      {feature.title}
                    </Typography>
                    
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ lineHeight: 1.6 }}
                    >
                      {feature.description}
                    </Typography>
                  </CardContent>
                  
                  <CardActions sx={{ justifyContent: 'center', pb: 3 }}>
                    <Button
                      variant="outlined"
                      sx={{
                        borderColor: feature.color,
                        color: feature.color,
                        '&:hover': {
                          borderColor: feature.color,
                          backgroundColor: `${feature.color}15`
                        }
                      }}
                    >
                      Bắt đầu
                    </Button>
                  </CardActions>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </motion.div>
    </Container>
  )
}

export default Home